/**
 * Document artifact — the shared, TipTap-backed markdown surface.
 *
 * This is the single component both surfaces render `kind: "document"`
 * artifacts through, so a document looks identical in chat and the Library:
 *  - the chat artifact canvas ({@link DocumentCanvas}) drives it *editable*,
 *    wiring the returned editor into its toolbar and threading auto-save +
 *    streaming/update diff-highlight through the props;
 *  - the Library file-preview ({@link DocumentSourceRenderer}) drives it
 *    *read-only*, so opening a document from the Library matches the canvas
 *    typography exactly (same extensions, same `artifact-content` CSS) instead
 *    of a separate ReactMarkdown pass.
 *
 * It owns the editor instance, the markdown extension set, external-content
 * sync, and the post-update diff-highlight. Chrome (header, toolbar, download,
 * source-view toggle, auto-save persistence, scroll container) stays with the
 * caller — that differs between the canvas and the dialog.
 *
 * @module components/artifacts/document-artifact
 */

import { Highlight } from "@tiptap/extension-highlight";
import { Link } from "@tiptap/extension-link";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableRow } from "@tiptap/extension-table-row";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useRef } from "react";
import { Markdown } from "tiptap-markdown";

import { computeDiffRanges } from "@/lib/artifacts/diff-positions";
import { cn } from "@/lib/utils";

import {
  DiffHighlightExtension,
  clearDiffHighlight,
  setDiffHighlight,
} from "./diff-highlight-extension";
import "./artifact-editor.css";

/**
 * How long the diff-highlight bands stay visible after an update before
 * fading. Matches the calm post-update feel — long enough to notice what
 * changed, short enough to clear the way for reading.
 */
const HIGHLIGHT_FADE_MS = 5000;

/** Base editor typography — scoped `artifact-content` (see artifact-editor.css). */
const EDITOR_BASE_CLASS =
  "artifact-content focus:outline-none mx-auto w-full max-w-4xl px-4 py-6 lg:px-10 lg:py-10 min-h-full";

/**
 * Storage shape exposed by `tiptap-markdown`. Re-declared locally because the
 * package's types don't extend TipTap's `storage` map automatically.
 */
interface MarkdownStorage {
  getMarkdown: () => string;
}

/** Read the editor's markdown, falling back to plain text if the slot is absent. */
function readMarkdown(editor: Editor): string {
  const storage = (editor.storage as { markdown?: MarkdownStorage }).markdown;
  return storage ? storage.getMarkdown() : editor.getText();
}

/**
 * The document extension set — the single source of truth for how markdown
 * artifacts parse and render on every surface. Internal: both the chat canvas
 * and the Library drive the editor through {@link DocumentArtifact} below, so
 * the extension config never needs to leak out.
 */
function createDocumentExtensions(placeholder: string) {
  return [
    StarterKit.configure({
      // Disable StarterKit's link to avoid a duplicate with the standalone
      // Link extension below (which supports `openOnClick`).
      link: false,
    }),
    Link.configure({
      openOnClick: false,
      autolink: true,
      HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
    }),
    Placeholder.configure({ placeholder }),
    // User-applied highlights from the toolbar render as <mark>. Diff bands do
    // NOT go through this — they use ProseMirror Decorations (see below).
    Highlight.configure({ multicolor: false }),
    // Visual-only "what changed" overlay after an update. Holds a
    // DecorationSet, never mutates the doc.
    DiffHighlightExtension,
    // GFM tables → real <table> nodes (invoices, comparisons, schedules).
    Table.configure({
      resizable: false,
      HTMLAttributes: { class: "artifact-content-table" },
    }),
    TableRow,
    TableHeader,
    TableCell,
    Markdown.configure({
      html: false,
      tightLists: true,
      bulletListMarker: "-",
      linkify: true,
      breaks: false,
      transformPastedText: true,
      transformCopiedText: false,
    }),
  ];
}

export interface DocumentArtifactProps {
  /** Markdown body to render. */
  content: string;
  /**
   * Previous body of a just-finished update. When set (and different from
   * `content`), the change is diff-highlighted then faded. The chat canvas
   * threads the store's `previousContent`; the Library omits it (read-only).
   */
  previousContent?: string | null;
  /** Editable (chat canvas) vs read-only (Library). Defaults to read-only. */
  editable?: boolean;
  /** Fires with the latest markdown on every edit — the canvas debounces + saves. */
  onChange?: (markdown: string) => void;
  /** Receives the editor instance (or null) so the caller's toolbar can drive it. */
  onEditorReady?: (editor: Editor | null) => void;
  /** Called after the diff-highlight fades so the caller can clear its baseline. */
  onPreviousContentApplied?: () => void;
  /** Placeholder shown when the document is empty. */
  placeholder?: string;
  /** Extra classes merged onto the editor content area. */
  className?: string;
}

/**
 * Shared document editor/viewer. See the module doc for the editable (chat)
 * vs. read-only (Library) split.
 */
export function DocumentArtifact({
  content,
  previousContent = null,
  editable = false,
  onChange,
  onEditorReady,
  onPreviousContentApplied,
  placeholder = "Start writing your document…",
  className,
}: DocumentArtifactProps) {
  /** Latest markdown we ourselves emitted — lets external sync skip echoes. */
  const lastEmittedRef = useRef(content);
  // Ref-mirror the callbacks so the editor config / effects don't churn when a
  // parent passes fresh closures each render.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onEditorReadyRef = useRef(onEditorReady);
  onEditorReadyRef.current = onEditorReady;
  const onPrevAppliedRef = useRef(onPreviousContentApplied);
  onPrevAppliedRef.current = onPreviousContentApplied;

  const editor = useEditor({
    extensions: createDocumentExtensions(placeholder),
    content,
    editable,
    editorProps: {
      attributes: { class: cn(EDITOR_BASE_CLASS, className) },
    },
    onUpdate({ editor }) {
      const next = readMarkdown(editor);
      lastEmittedRef.current = next;
      onChangeRef.current?.(next);
    },
  });

  /**
   * Hand the editor up to the caller for toolbar wiring, and null it on
   * unmount (e.g. the canvas toggling to source view) so the caller never
   * drives a destroyed editor.
   */
  useEffect(() => {
    onEditorReadyRef.current?.(editor ?? null);
    return () => onEditorReadyRef.current?.(null);
  }, [editor]);

  /**
   * Sync external content changes (streaming deltas, server hydration, update
   * finish) into the editor when they don't echo our own edit.
   *
   * Diff-highlight mode (chat updates): `previousContent` set means an update
   * just landed — snapshot the rendered text, swap atomically, overlay the
   * inserted ranges as decorations, then fade. Normal sync: push the latest
   * content with `emitUpdate: false` so the round-trip doesn't re-fire onChange.
   */
  useEffect(() => {
    if (!editor) return;
    const next = content;
    const prev = previousContent;

    if (prev != null && next.length > 0 && next !== prev) {
      const oldDocText = editor.state.doc.textContent;
      editor.commands.setContent(next, { emitUpdate: false });
      lastEmittedRef.current = next;
      const ranges = computeDiffRanges(oldDocText, editor.state.doc);
      setDiffHighlight(editor, ranges);
      const fadeId = window.setTimeout(() => {
        clearDiffHighlight(editor);
        onPrevAppliedRef.current?.();
      }, HIGHLIGHT_FADE_MS);
      return () => window.clearTimeout(fadeId);
    }

    if (next === lastEmittedRef.current) return;
    editor.commands.setContent(next, { emitUpdate: false });
    lastEmittedRef.current = next;
  }, [editor, content, previousContent]);

  /** Keep `editable` live so streaming → idle flips without a remount. */
  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  return <EditorContent editor={editor} />;
}
